import { useState, useEffect, useCallback } from 'react';
import { patientViewPath } from '../fetcher';
import { readConsultationNavigation, clearConsultationNavigation } from './consultationNavigationSession';

// Session storage keys for tracking where a consultation was opened from
const CONSULTATION_FROM_PATIENT_KEY = 'consultationFromPatient';
const CONSULTATION_FROM_DOCUMENT_KEY = 'consultationFromDocument';

/**
 * Prev/next through the scribe list, auto-advance after filing, and the "opened from" context
 * (patient or document) that decides where Back goes. Every exit asks before leaving a recording.
 */
const useConsultationNavigation = ({
  consultationId,
  location,
  navigate,
  backPatientId,
  confirmLeaveRecording,
}) => {
  const [fromPatientNhsId, setFromPatientNhsId] = useState(null);
  const [fromPatientId, setFromPatientId] = useState(null);
  const [fromDocumentId, setFromDocumentId] = useState(null);
  const [navigationContext, setNavigationContext] = useState(null);

  // Auto-navigate to next (or prev at end of list) when current consultation transitions
  // QUEUED_FOR_FILING → FILED_AWAY. Only for the user who filed it away.
  const [shouldAutoNavigate, setShouldAutoNavigate] = useState(false);
  const [prevButtonAnimating, setPrevButtonAnimating] = useState(false);
  const [nextButtonAnimating, setNextButtonAnimating] = useState(false);

  useEffect(() => {
    setShouldAutoNavigate(false);
    setPrevButtonAnimating(false);
    setNextButtonAnimating(false);
  }, [consultationId]);

  // Handle navigation context - check location.state and session storage
  useEffect(() => {
    try {
      const patientContextFromState = location.state?.fromPatient;
      const patientNhsIdFromState = location.state?.patientNhsId;
      const patientIdFromState = location.state?.patientId;
      const patientNameFromState = location.state?.patientName;
      const documentIdFromState = location.state?.fromDocumentId;

      if (patientContextFromState && patientNhsIdFromState) {
        setFromPatientNhsId(patientNhsIdFromState);
        setFromPatientId(patientIdFromState || null);
        sessionStorage.setItem(CONSULTATION_FROM_PATIENT_KEY, JSON.stringify({
          nhsId: patientNhsIdFromState,
          patientId: patientIdFromState || null,
          name: patientNameFromState
        }));
      } else {
        // Check session storage for persisted context
        const patientContextStr = sessionStorage.getItem(CONSULTATION_FROM_PATIENT_KEY);
        if (patientContextStr) {
          try {
            const patientContext = JSON.parse(patientContextStr);
            setFromPatientNhsId(patientContext.nhsId);
            setFromPatientId(patientContext.patientId || null);
          } catch (e) {
            console.error('Error parsing patient context from session storage:', e);
          }
        }
      }

      if (documentIdFromState) {
        setFromDocumentId(documentIdFromState);
        sessionStorage.setItem(CONSULTATION_FROM_DOCUMENT_KEY, documentIdFromState);
      } else {
        const documentContext = sessionStorage.getItem(CONSULTATION_FROM_DOCUMENT_KEY);
        if (documentContext) {
          setFromDocumentId(documentContext);
        }
      }
    } catch (error) {
      console.error('Error handling navigation context:', error);
    }
  }, [consultationId, location.state]);

  // Read navigation context from session storage on mount and when consultationId changes
  useEffect(() => {
    setNavigationContext(readConsultationNavigation(consultationId));
  }, [consultationId]);

  const navigateToIndex = useCallback((newIndex, options) => {
    if (!confirmLeaveRecording()) return;

    const targetConsultationId = navigationContext.consultationIds[newIndex];
    const navState = {};
    if (options.autoShowDetailsTab) {
      navState.autoShowDetailsTab = true;
    }

    navigate(`/consultation-detail/${targetConsultationId}`, { state: navState });
  }, [navigationContext, navigate, confirmLeaveRecording]);

  const handleNavigatePrev = useCallback((options = {}) => {
    if (!navigationContext || navigationContext.currentIndex <= 0) return;
    navigateToIndex(navigationContext.currentIndex - 1, options);
  }, [navigationContext, navigateToIndex]);

  const handleNavigateNext = useCallback((options = {}) => {
    if (!navigationContext || navigationContext.currentIndex >= navigationContext.consultationIds.length - 1) return;
    navigateToIndex(navigationContext.currentIndex + 1, options);
  }, [navigationContext, navigateToIndex]);

  /** Called when the current user's filing of this consultation completes. */
  const requestAutoNavigate = useCallback(() => setShouldAutoNavigate(true), []);

  // When QUEUED_FOR_FILING → FILED_AWAY for the filing user, animate Next (or Prev at end of list)
  // then navigate. Runs with a fresh navigationContext rather than one captured by the update stream.
  useEffect(() => {
    if (!shouldAutoNavigate) return undefined;

    const canNavigateNext = navigationContext && navigationContext.currentIndex < navigationContext.consultationIds.length - 1;
    const canNavigatePrev = navigationContext && navigationContext.currentIndex > 0;

    if (canNavigateNext) {
      setNextButtonAnimating(true);
      const timer = setTimeout(() => {
        setNextButtonAnimating(false);
        setShouldAutoNavigate(false);
        handleNavigateNext({ autoShowDetailsTab: true });
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (canNavigatePrev) {
      setPrevButtonAnimating(true);
      const timer = setTimeout(() => {
        setPrevButtonAnimating(false);
        setShouldAutoNavigate(false);
        handleNavigatePrev({ autoShowDetailsTab: true });
      }, 1000);
      return () => clearTimeout(timer);
    }

    setShouldAutoNavigate(false);
    return undefined;
  }, [shouldAutoNavigate, navigationContext, handleNavigateNext, handleNavigatePrev]);

  const handleBackClick = useCallback(() => {
    if (!confirmLeaveRecording()) return;

    try {
      sessionStorage.removeItem(CONSULTATION_FROM_PATIENT_KEY);
      sessionStorage.removeItem(CONSULTATION_FROM_DOCUMENT_KEY);
    } catch (e) {
      console.error('Error clearing session storage:', e);
    }
    clearConsultationNavigation();

    if (fromPatientNhsId) {
      const patientIdForBack = fromPatientId || backPatientId;
      navigate(
        patientIdForBack ? patientViewPath(fromPatientNhsId, patientIdForBack) : `/patient/${fromPatientNhsId}`,
        {
          state: {
            highlightConsultationId: consultationId
          }
        }
      );
    } else if (fromDocumentId) {
      // Came from a document (via PatientDiaryPanel)
      navigate(`/document-detail/${fromDocumentId}`);
    } else {
      navigate('/', { state: { selectTab: 'scribe', highlightConsultationId: consultationId } });
    }
  }, [confirmLeaveRecording, fromPatientNhsId, fromPatientId, backPatientId, fromDocumentId, navigate, consultationId]);

  const backLabel = fromPatientNhsId ? 'Back to Patient' : fromDocumentId ? 'Back to Document' : 'Back to Scribe';

  return {
    navigationContext,
    handleNavigatePrev,
    handleNavigateNext,
    prevButtonAnimating,
    nextButtonAnimating,
    requestAutoNavigate,
    handleBackClick,
    backLabel,
  };
};

export default useConsultationNavigation;
